import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { HexColorPicker } from 'react-colorful';
import type { FabricCanvas } from '../../types/fabric';
import type { PrintableColor } from '@customedge/shared';
import { useDesignStore } from '../../store/designStore';
import { useColorBudget, normalizeColor, replaceColorEverywhere, countObjectsUsingColor, getTopDesignColors } from '../../hooks/useColorBudget';
import { safeGetItem, safeSetItem } from '../../lib/safeStorage';
import { usePopoverPosition, PortaledPopover } from '../tools/ColorPicker';
import { ToolSection } from '../tools/shared/ToolSection';
import { InfoTooltip } from '../tools/shared/InfoTooltip';
import { nearestPantone, PmsReadout } from '../tools/PantoneMatch';
import { useConfirm } from '../Notifications';

async function fetchPrintableColors(): Promise<PrintableColor[]> {
  const r = await fetch('/api/printable-colors');
  const d = await r.json();
  return (d.colors ?? []) as PrintableColor[];
}

interface ColorsUsedPanelProps {
  canvas: FabricCanvas | null;
}

// One slot per print-color the active side's Print Color count allows —
// either FILLED (backed by a real color currently used by some object/the
// background on this side) or RESERVED (no object uses it yet; the customer
// picked it ahead of time, or it was auto-filled from the design's top
// colors the moment the count grew to include this slot — see
// getTopDesignColors). Slots beyond a Full-Color/unlimited budget aren't a
// meaningful concept (no fixed count to size the grid to), so that case
// falls back to just showing whatever's actually used, one slot each, with
// no reserved/empty slots at all.
interface ColorSlot {
  index: number;
  hex: string | null; // null = reserved, no color assigned yet (renders transparent per direct user request)
  isReserved: boolean;
}

const STORAGE_PREFIX = 'customedge_reserved_slots';

function reservedStorageKey(sideKey: string, countKey: string): string {
  return `${STORAGE_PREFIX}:${sideKey}:${countKey}`;
}

function loadReserved(sideKey: string, countKey: string): string[] {
  const raw = safeGetItem('session', reservedStorageKey(sideKey, countKey));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function saveReserved(sideKey: string, countKey: string, hexes: string[]): void {
  safeSetItem('session', reservedStorageKey(sideKey, countKey), JSON.stringify(hexes));
}

// Pure "what should the newly-opened slot(s) default to" computation —
// extracted from the auto-fill effect below so it's independently unit
// testable without mounting a full component/effect cycle. Cycles through
// `topColors` (already ranked most-to-least-used, see getTopDesignColors),
// skipping any color already occupying an existing slot (either a real used
// color or a previously reserved one) — per direct user request/
// AskUserQuestion confirmation, each new slot gets the NEXT top color, not
// the same single one repeated.
export function computeAutoFillColors(topColors: string[], alreadyAssignedHexes: string[], newSlotCount: number): string[] {
  const assignedKeys = new Set(alreadyAssignedHexes.map(normalizeColor).filter((k): k is string => k != null));
  const candidates = topColors.filter(c => {
    const key = normalizeColor(c);
    return key != null && !assignedKeys.has(key);
  });
  return candidates.slice(0, Math.max(0, newSlotCount));
}

// Right-panel section (RightPanel.tsx, between PropertyPanel and LayerPanel)
// showing every color slot the active side's print-color count allows, per
// direct user request: "we can introduce one more section over the right
// side object list and edit. Color Used in Design.. There also user can
// replace the color like above mentioned point." Clicking a FILLED slot
// opens the same picker every other color control in this app uses and
// replaces that color on every object currently using it (replaceColorEverywhere)
// — "If user change the color it will reflect on all the object user the
// color." Clicking a RESERVED (empty) slot opens the picker pre-populated
// with the design's own top-10 most-used colors as quick-picks and just
// records the pick as reserved (doesn't paint anything) — confirmed via
// AskUserQuestion this is a "reserved palette," not something that needs to
// apply anywhere immediately.
export function ColorsUsedPanel({ canvas }: ColorsUsedPanelProps) {
  const { t } = useTranslation();
  const activeSide = useDesignStore(s => s.sides.find(side => side.sideKey === s.activeSideKey));
  const budget = useColorBudget(activeSide);
  const confirm = useConfirm();
  const [, forceUpdate] = useState(0);
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const sideKey = activeSide?.sideKey;
  const countKey = budget.countKey;

  // Re-renders when the canvas changes so a slot picked as "reserved" (which
  // doesn't touch the canvas) still shows immediately, and so switching sides
  // re-reads that side's own reserved set from storage.
  useEffect(() => { forceUpdate(n => n + 1); }, [sideKey, budget.limit]);

  // Auto-fills newly-opened slot(s) the moment the count grows — per direct
  // user request: "user selected 2 color. then they switch to 3 color it
  // will add the new color selection area. through that user can select the
  // new color. You maintain the Top 10 colors from all the objects you can
  // use those color for auto file when the user add more color while
  // editing." Tracks the PREVIOUS limit per side+count-key combination (a
  // ref, not state — this must never itself trigger a re-render) so this
  // only fires on a genuine increase, never on mount/side-switch (where
  // "previous" is simply unknown, not smaller) and never twice for the same
  // transition (React 18 StrictMode double-invoke safe, same reasoning
  // BackgroundColorTool.tsx's autoQuantizeFiredRef already documents).
  // Cycles through the design's top colors (getTopDesignColors) — confirmed
  // via AskUserQuestion each new slot should get the NEXT most-used color not
  // already assigned to another slot, not the same single top color repeated.
  const prevLimitRef = useRef<{ key: string; limit: number } | null>(null);
  useEffect(() => {
    if (!sideKey || budget.limit == null) { prevLimitRef.current = null; return; }
    const trackKey = `${sideKey}:${countKey}`;
    const prev = prevLimitRef.current;
    prevLimitRef.current = { key: trackKey, limit: budget.limit };
    if (!prev || prev.key !== trackKey) return; // first visit to this side+count — nothing to compare against
    if (budget.limit <= prev.limit) return; // count shrank or stayed the same — nothing to fill

    const existingReserved = loadReserved(sideKey, countKey);
    const toAdd = computeAutoFillColors(
      getTopDesignColors(10),
      [...budget.usedColors, ...existingReserved],
      budget.limit - prev.limit,
    );
    if (toAdd.length === 0) return;
    saveReserved(sideKey, countKey, [...existingReserved, ...toAdd]);
    forceUpdate(n => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideKey, countKey, budget.limit]);

  if (!activeSide) return null;

  // Full Color (or an unconfigured product) has no fixed slot COUNT to size a
  // grid to — show whatever's actually used, one slot each, no reserved/empty
  // slots (there's no "next slot" concept without a limit).
  const slotCount = budget.limit ?? budget.usedColors.length;
  if (slotCount === 0) {
    return (
      <div style={{ padding: '12px 12px 0' }}>
        <ToolSection title={t('colorsUsed.title')} divider={false}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>{t('colorsUsed.empty')}</p>
        </ToolSection>
      </div>
    );
  }

  const reserved = loadReserved(activeSide.sideKey, countKey)
    .filter(hex => !budget.usedColors.some(u => normalizeColor(u) === normalizeColor(hex)));

  // Build the slot list: real used colors first (stable order — first-seen
  // on canvas), then reserved-but-unused colors, then any remaining slots as
  // fully empty (no color at all yet, no pick ever made for them).
  const filled: ColorSlot[] = budget.usedColors.map((hex, i) => ({ index: i, hex, isReserved: false }));
  const reservedSlots: ColorSlot[] = reserved
    .slice(0, Math.max(0, slotCount - filled.length))
    .map((hex, i) => ({ index: filled.length + i, hex, isReserved: true }));
  const emptyCount = Math.max(0, slotCount - filled.length - reservedSlots.length);
  const emptySlots: ColorSlot[] = Array.from({ length: emptyCount }, (_, i) => ({
    index: filled.length + reservedSlots.length + i, hex: null, isReserved: true,
  }));
  const slots = [...filled, ...reservedSlots, ...emptySlots];

  const assignReserved = (slot: ColorSlot, hex: string) => {
    const next = [...reserved];
    if (slot.hex) {
      const at = next.findIndex(h => normalizeColor(h) === normalizeColor(slot.hex));
      if (at >= 0) next[at] = hex; else next.push(hex);
    } else {
      next.push(hex);
    }
    saveReserved(activeSide.sideKey, countKey, next);
    setOpenSlot(null);
    forceUpdate(n => n + 1);
  };

  // Picking a new color for an already-FILLED slot repaints every object on
  // this side currently using that color (replaceColorEverywhere) — a real,
  // possibly-surprising side effect if the customer only meant to change
  // ONE object's color and didn't realize another object already shared it.
  // Per direct user request ("if you change the used color it will change
  // the color in existing objects color" — asked as a warn-first flow, not
  // silent propagation): count how many things this color touches first; if
  // it's only the one object this slot obviously already represents (the
  // common case — e.g. the very first pick for a newly reserved slot, or a
  // single text object nobody else shares a color with), apply immediately,
  // nothing to warn about. `objectCount` here includes the slot's OWN
  // object, so the confirm threshold is "more than 1 usage total"
  // (2+ objects, or 1 object + the background, etc) — NOT "objectCount > 0",
  // which would wrongly confirm on every single edit (a slot's hex always
  // matches at least the one object it came from).
  const replaceFilled = async (fromHex: string, toHex: string) => {
    if (!canvas || !activeSide) return;
    const { objectCount, usesBackground } = countObjectsUsingColor(canvas, activeSide, fromHex);
    const otherUsageCount = objectCount + (usesBackground ? 1 : 0) - 1; // -1 excludes this slot's own object
    if (otherUsageCount > 0) {
      const ok = await confirm(t('colorsUsed.confirmReplaceBody', { count: otherUsageCount }), {
        confirmLabel: t('colorsUsed.confirmReplaceButton'),
        danger: false,
      });
      if (!ok) return;
    }
    replaceColorEverywhere(canvas, activeSide, fromHex, toHex);
    setOpenSlot(null);
    forceUpdate(n => n + 1);
  };

  return (
    <div style={{ padding: '12px 12px 12px' }}>
      <ToolSection
        title={t('colorsUsed.title')}
        divider={false}
        titleExtra={budget.limit != null ? <InfoTooltip text={t('colorsUsed.replaceHint')} /> : undefined}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {slots.map(slot => (
            <SlotSwatch
              key={slot.index}
              slot={slot}
              // The OTHER slots' own current colors — "Colors used in this
              // design" as a picker source distinct from the design-wide
              // top-10, per direct user request ("give option replace color
              // onselect it will give 'Colors used in this design' once select
              // the color swatch it will give color pallete or top 10 colors
              // used in design"): shown FIRST as the primary quick-pick list,
              // with top-10 as a secondary fallback below it (see SlotSwatch).
              designColors={slots.filter(s => s.index !== slot.index && s.hex).map(s => s.hex!)}
              isOpen={openSlot === slot.index}
              onToggle={() => setOpenSlot(o => (o === slot.index ? null : slot.index))}
              onClose={() => setOpenSlot(null)}
              onPick={hex => (slot.isReserved ? assignReserved(slot, hex) : replaceFilled(slot.hex!, hex))}
            />
          ))}
        </div>
      </ToolSection>
    </div>
  );
}

// const SLOT_POPOVER_WIDTH = 200;
// const SLOT_POPOVER_HEIGHT = 380;
const SLOT_POPOVER_WIDTH = 240;
const SLOT_POPOVER_HEIGHT = 380;
function SlotSwatch({ slot, designColors, isOpen, onToggle, onClose, onPick }: {
  slot: ColorSlot;
  designColors: string[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: (hex: string) => void;
}) {
  const { t } = useTranslation();
  const { triggerRef, pos } = usePopoverPosition(isOpen, SLOT_POPOVER_WIDTH, SLOT_POPOVER_HEIGHT);
  const [draft, setDraft] = useState(slot.hex ?? '#000000');
  useEffect(() => { setDraft(slot.hex ?? '#000000'); }, [slot.hex, isOpen]);
  // PMS readout only, per direct user request that PMS show up on every
  // color-selection surface — this is the "Colors Used in Design" panel's
  // own per-slot popover equivalent of BackgroundColorTool.tsx's swatch editor.
  const { data: printableColors = [] } = useQuery({
    queryKey: ['printable-colors'],
    queryFn: fetchPrintableColors,
    staleTime: 300_000,
    enabled: isOpen,
  });

  // Freshly computed each open — the design's colors can have changed since
  // the last time this slot's popover was opened. Top-10 excludes whatever's
  // already offered in the "Colors used in this design" row above it (the
  // OTHER slots) so the same swatch never appears twice in one popover.
  const designColorKeys = new Set(designColors.map(normalizeColor).filter((k): k is string => k != null));
  const topColors = isOpen ? getTopDesignColors(10).filter(c => !designColorKeys.has(normalizeColor(c)!)) : [];

  return (
    <>
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onClick={onToggle}
        title={slot.hex ?? t('colorsUsed.reserved')}
        style={{
          width: 30, height: 30, borderRadius: 6, cursor: 'pointer', padding: 0,
          border: slot.isReserved ? '1px dashed var(--border)' : '1px solid var(--border)',
          // No color assigned at all yet — checkerboard, per direct user
          // request ("If no object is selected then keep that as
          // transperant"). A reserved slot that already has a picked color
          // shows that color with a dashed border (still not "in use", but no
          // longer blank either).
          background: slot.hex
            ? slot.hex
            : 'repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 0 0 / 8px 8px',
        }}
      />
      {isOpen && pos && (
        <PortaledPopover top={pos.top} left={pos.left} width={SLOT_POPOVER_WIDTH} onClose={onClose}>
          {slot.isReserved && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('colorsUsed.reserved')}</p>
          )}
          {designColors.length > 0 && (
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 4px' }}>{t('colorsUsed.title')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {designColors.map(c => (
                  <button key={c} onClick={() => onPick(c)} title={c}
                    style={{ width: 18, height: 18, background: c, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, cursor: 'pointer', padding: 0 }} />
                ))}
              </div>
            </div>
          )}
          {topColors.length > 0 && (
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 4px' }}>{t('colorsUsed.topColors')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {topColors.map(c => (
                  <button key={c} onClick={() => onPick(c)} title={c}
                    style={{ width: 18, height: 18, background: c, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, cursor: 'pointer', padding: 0 }} />
                ))}
              </div>
            </div>
          )}
          <HexColorPicker color={draft} onChange={setDraft} style={{ width: '100%' }} />
          {/* <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              style={{ flex: 1, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}
            />
            <button
              onClick={() => onPick(draft)}
              style={{ padding: '4px 10px', border: 'none', borderRadius: 4, background: 'var(--primary)', color: 'white', cursor: 'pointer', fontSize: 11 }}
            >
              OK
            </button>
          </div> */}
          <div
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    minWidth: 0,
  }}
>
  <input
    value={draft}
    onChange={e => setDraft(e.target.value)}
    style={{
      flex: 1,
      minWidth: 0,
      width: '100%',
      padding: '4px 6px',
      border: '1px solid var(--border)',
      borderRadius: 4,
      fontSize: 11,
      fontFamily: 'monospace',
      boxSizing: 'border-box',
    }}
  />

  <button
    type="button"
    onClick={() => onPick(draft)}
    style={{
      flexShrink: 0,
      padding: '4px 10px',
      border: 'none',
      borderRadius: 4,
      background: 'var(--primary)',
      color: 'white',
      cursor: 'pointer',
      fontSize: 11,
      whiteSpace: 'nowrap',
    }}
  >
    OK
  </button>
</div>
          {printableColors.length > 0 && (
            <PmsReadout match={nearestPantone(draft, printableColors)} />
          )}
        </PortaledPopover>
      )}
    </>
  );
}

